import {
  FC,
  memo,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  lazy,
  Suspense,
} from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { match } from "ts-pattern";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useLazyQuery } from "@apollo/client";
import { useDialog } from "@/hooks/useDialog";
import { CompaniesExploreList } from "./CompaniesExploreList";
import { SegmentDialog } from "@/components/Segment/SegmentDialog";
import { SegmentModificationDialogs } from "@/components/Segment/SegmentModificationDialogs";
import CreditProgressBar from "@/components/Credits/CreditsProgressBar";
import { useSnackbar } from "@/components/Snackbar";
import {
  deleteSegment,
  fetchSegments,
  getSegment,
  updateSegment,
} from "@/apis/bacchus/segment";
import { SEARCH_COMPANY_QUERY } from "@/apis/companySearch/searchCompany";
import { COMPANY_SEARCH_SLIM_QUERY } from "@/apis/companySearch/companyList";
import { Segment } from "@/apis/bacchus/types";
import { useTaxonomyContext } from "@/contexts/Taxonomy/TaxonomyContext";
import {
  CompanyFilterProvider,
  useCompanyFiltersController,
  FiltersSidebar,
  FilterPanel,
  FiltersChipsBar,
  CompanyExportModal,
  modules,
  FILTER_TITLES,
  FILTER_CATEGORIES,
  buildCompanyFilterLookups,
  useUploadedAccountsSync,
  isModuleKey,
  type FilterKey,
  type GeographyOptions,
  type ItSpendOptions,
  type RegionId,
  type FilterLookups,
  type TargetOrganizationsOptions,
  type UploadedAccountsOptions,
  type IntentOptions,
} from "@/lib/company-filter";
import { safeParseFilters } from "@/lib/company-filter/utils/safeParse";
import { getScoringFilterStatus } from "@/lib/company-filter/modules/scoringFilterStatus";
import { useFeatureFlag } from "@/hooks/useLaunchDarkly";
import { isUniverseCategoryVisible } from "@/lib/company-filter/universeCategoryGates";
import { InstallAgeSetting } from "@/lib/company-filter/modules/companyConfig";
import { useScoringMethodModal } from "@/hooks/useScoringMethodModal";
import {
  useExportDisabledMessage,
  useExportRequiresFilters,
  useExportVisibility,
} from "@/hooks/useExportVisibility";
import { useScoringStore } from "@/store/scoringStore";
import { getLoggedUserProfile, getTenantId } from "@/utils/auth";
import { openAiScoring } from "@/utils/module-urls";
import { useLocalStorage } from "@/utils/useLocalStorage";
import type { ScoringProfile, ScoringType } from "@/lib/scoring/types";

const ScoringPanel = lazy(() =>
  import("@/lib/scoring/components/ScoringPanel").then((m) => ({
    default: m.ScoringPanel,
  }))
);
const ScoringProfileDialog = lazy(() =>
  import("@/lib/scoring/components/ScoringProfileDialog").then((m) => ({
    default: m.ScoringProfileDialog,
  }))
);
const ScoringWizardDialog = lazy(() =>
  import("@/lib/scoring/components/ScoringWizardDialog").then((m) => ({
    default: m.ScoringWizardDialog,
  }))
);
import { LightTooltip } from "@/components/LightTooltip";
import { SegmentBreadcrumb } from "../components/SegmentBreadcrumb";
import { UnsavedSegmentChangesDialog } from "../components/UnsavedSegmentChangesDialog";
import { useSegmentNavigationBlocker } from "../hooks/useSegmentNavigationBlocker";
import { addRecentSegment } from "@/utils/recentSegments";
import {
  COMPANY_SEARCH_CLIENT,
  COMPANY_MATCH_CLIENT,
  TAXONOMY_CLIENT,
} from "@/utils/consts";
import { useCommandPaletteStore } from "@/store/commandPalette/commandPaletteStore";
import {
  useUploadLazyQuery,
  useStatsLazyQuery,
} from "@/generated/company-match";
import { downloadMatchDetails } from "@/apis/companyMatch/restApi";
import { AxiosError } from "axios";
import {
  useGetIntentBuyersJourneyQuery,
  useGetIntentContextTypesQuery,
} from "@/generated/taxonomy";
import type { CompanySearchResponse } from "@/types/companySearch";
import type { SpogCompany } from "@/legacy/types/SpogCompany";
import { CompaniesExploreSearchInput } from "./components/CompaniesExploreSearchInput";

const LOCALIZE_INSTALLS_STORAGE_KEY = "localizeInstalls";

const SEGMENT_ERROR_MESSAGES = {
  NOT_FOUND: "Segment not found",
  NO_ACCESS: "You don't have access to this segment",
  GENERIC:
    "Unable to load segment. Please try again or contact support if the problem persists.",
} as const;

type SearchCompaniesV2Response = {
  searchCompaniesV2?: {
    data?: SpogCompany[];
    count?: number;
  };
};

function spogCompanyToCached(company: SpogCompany) {
  return {
    hgId: company.hgId,
    name: company.name,
    url: company.url ?? undefined,
  };
}

const LocalizedInstallsFooter: FC<{
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}> = memo(({ checked, onChange }) => {
  const tooltipTitle = (
    <Box sx={{ p: 0.5, maxWidth: 320 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
        Localized Installs
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.75 }}>
        Only return companies where install data is detected in the same country
        as the company (Recommended)
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", lineHeight: 1.5 }}
      >
        Disabling this option will return companies if an install is detected
        anywhere in the company's global footprint, even outside the country of
        the company. Applicable when filtering by technographic data.
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <InstallAgeSetting />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" fontWeight={600}>
          Localized Installs
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Switch
            checked={checked}
            onChange={onChange}
            disableRipple
            size="small"
          />
          <LightTooltip title={tooltipTitle} placement="top">
            <IconButton
              className="localized-installs-info"
              size="small"
              sx={{
                color: "text.secondary",
                opacity: 0.6,
                transition: "opacity 150ms ease",
                "&:hover, &:focus-visible": { opacity: 1 },
              }}
              aria-label="Localized installs info"
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </LightTooltip>
        </Stack>
      </Stack>
    </Box>
  );
});

LocalizedInstallsFooter.displayName = "LocalizedInstallsFooter";

const CompaniesExplorePageContent: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { segmentId } = useParams<{ segmentId: string }>();
  const { showInfo, showSuccess, showError } = useSnackbar();
  const addRecentItem = useCommandPaletteStore((s) => s.addRecentItem);

  const urlSearch = searchParams.get("search") ?? "";

  // Sync hooks for filters that need async data from server
  useUploadedAccountsSync();

  // Feature flags for filter visibility
  const intentFiltersEnabled = useFeatureFlag(
    "marketAnalyzerIntentFiltersEnabled",
    false
  );
  const scoringEnabled = useFeatureFlag("marketAnalyzerScoringEnabled", false);
  const universeJsonExportEnabled = useFeatureFlag("universeJsonExport", false);
  const showMethodModal = useScoringMethodModal();

  // Scoring store state for dynamic filter enablement
  const activeProfileIds = useScoringStore((s) => s.activeProfileIds);
  const profiles = useScoringStore((s) => s.profiles);

  // Filter categories based on feature flags and scoring state
  const filteredCategories = useMemo(
    () =>
      FILTER_CATEGORIES.filter((cat) => {
        if (cat.id === "intent" && !intentFiltersEnabled) return false;
        if (
          (cat.id === "fitScore" || cat.id === "intentScore") &&
          !scoringEnabled
        )
          return false;
        return isUniverseCategoryVisible(cat.id, universeJsonExportEnabled);
      }).map((cat) => {
        if (cat.id !== "fitScore" && cat.id !== "intentScore") return cat;
        const status = getScoringFilterStatus(
          cat.id === "fitScore" ? "fit" : "intent",
          activeProfileIds,
          profiles
        );
        return {
          ...cat,
          enabled: status.enabled,
          disabledReason: status.disabledReason ?? undefined,
        };
      }),
    [
      intentFiltersEnabled,
      scoringEnabled,
      universeJsonExportEnabled,
      activeProfileIds,
      profiles,
    ]
  );

  const isCreateMode = searchParams.get("action") === "new";
  const isEditMode = !!segmentId;
  const isExploreMode = !isCreateMode && !isEditMode;

  const saveDialog = useDialog();
  const editDialog = useDialog<Segment>();
  const deleteDialog = useDialog();
  const exportDialog = useDialog();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const [activeFilter, setActiveFilter] = useState<FilterKey | null>(null);
  const [localizeInstalls, setLocalizeInstalls] = useLocalStorage<boolean>(
    LOCALIZE_INSTALLS_STORAGE_KEY,
    true
  );

  const [segment, setSegment] = useState<Segment | null>(null);
  const [isLoadingSegment, setIsLoadingSegment] = useState(!!segmentId);
  const [companyCount, setCompanyCount] = useState<number>(0);
  const [allSegments, setAllSegments] = useState<Segment[]>([]);
  const [activeSortEntry, setActiveSortEntry] = useState<{
    field: string;
    direction: string;
  }>({ field: "revenue_range_min", direction: "desc" });
  // Stable callback + skip-equal-update guards against an infinite render
  // loop: CompaniesExploreList's effect that calls onActiveSortChange has
  // the callback in its deps, and setState with a fresh object literal would
  // re-render the page → re-create the inline arrow → effect refires.
  const handleActiveSortChange = useCallback(
    (field: string, direction: string) =>
      setActiveSortEntry((prev) =>
        prev.field === field && prev.direction === direction
          ? prev
          : { field, direction }
      ),
    []
  );
  const canExport = useExportVisibility();
  const exportRequiresFilters = useExportRequiresFilters();
  const exportDisabledMessage = useExportDisabledMessage();

  // Extract permissions from segment
  const canEdit = segment?.permissions?.write ?? false;
  const canDelete = segment?.permissions?.delete ?? false;

  const pageTitle = match({ isCreateMode, isEditMode })
    .with({ isCreateMode: true }, () => "New Segment")
    .with({ isEditMode: true }, () => segment?.name ?? "")
    .otherwise(() => "Companies");

  const breadcrumbMode = match({ isCreateMode, isEditMode })
    .with({ isCreateMode: true }, () => "create" as const)
    .with({ isEditMode: true }, () => "edit" as const)
    .otherwise(() => "browse" as const);

  // Determine save button configuration based on mode and permissions
  // Note: While loading in edit mode, we show "Save Segment" to avoid UI flicker
  const saveButtonConfig = match({
    isCreateMode,
    isExploreMode,
    isLoadingSegment,
    canEdit,
  })
    .with({ isCreateMode: true }, () => ({
      action: "create" as const,
      label: "Create Segment",
    }))
    .with({ isExploreMode: true }, () => ({
      action: "save" as const,
      label: "Save Segment",
    }))
    .with({ isLoadingSegment: true }, () => ({
      action: "saveFilters" as const,
      label: "Save Segment",
    }))
    .with({ canEdit: true }, () => ({
      action: "saveFilters" as const,
      label: "Save Segment",
    }))
    .otherwise(() => ({
      action: "saveAsNew" as const,
      label: "Save as New",
    }));

  // Determine which actions are available in the dropdown menu
  // Note: showSaveAsNew is false when it's already the primary button action
  const menuActions = match({ canEdit, canDelete })
    .with({ canEdit: false, canDelete: false }, () => ({
      showSaveAsNew: false,
      showDivider: false,
      showEdit: false,
      showDelete: false,
    }))
    .with({ canEdit: true, canDelete: true }, () => ({
      showSaveAsNew: true,
      showDivider: true,
      showEdit: true,
      showDelete: true,
    }))
    .with({ canEdit: true }, () => ({
      showSaveAsNew: true,
      showDivider: true,
      showEdit: true,
      showDelete: false,
    }))
    .with({ canDelete: true }, () => ({
      showSaveAsNew: false,
      showDivider: true,
      showEdit: false,
      showDelete: true,
    }))
    .exhaustive();

  const {
    appliedFilters,
    hasUnsavedChanges,
    chips: filterChips,
    getFilterCount,
    hydrateFromCompanyFilters,
    serializeAppliedToJson,
    markAsPersisted,
    clearAll,
  } = useCompanyFiltersController();

  // Enrich score filter chips with tooltip when filter is disabled but has values
  const enrichedChips = useMemo(() => {
    const fitCat = filteredCategories.find((c) => c.id === "fitScore");
    const intentCat = filteredCategories.find((c) => c.id === "intentScore");
    return filterChips.map((chip) => {
      if (
        chip.filterKey === "fitScore" &&
        fitCat &&
        !fitCat.enabled &&
        fitCat.disabledReason
      ) {
        return {
          ...chip,
          tooltipContent: fitCat.disabledReason,
          disabled: true,
        };
      }
      if (
        chip.filterKey === "intentScore" &&
        intentCat &&
        !intentCat.enabled &&
        intentCat.disabledReason
      ) {
        return {
          ...chip,
          tooltipContent: intentCat.disabledReason,
          disabled: true,
        };
      }
      return chip;
    });
  }, [filterChips, filteredCategories]);

  // Navigation blocking for unsaved changes
  const { isBlocked, proceed, reset } =
    useSegmentNavigationBlocker(isSavingRef);

  // Browser close/refresh protection
  useEffect(() => {
    const hasChangesToProtect = isCreateMode
      ? appliedFilters !== undefined && Object.keys(appliedFilters).length > 0
      : hasUnsavedChanges;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasChangesToProtect) {
        event.preventDefault();
        event.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isCreateMode, appliedFilters, hasUnsavedChanges]);

  const handleDebouncedSearchChange = useCallback(
    (nextValue: string) => {
      const nextSearch = nextValue.trim();

      if (nextSearch === urlSearch) return;

      const nextParams = new URLSearchParams(searchParams);
      if (!nextSearch) {
        nextParams.delete("search");
      } else {
        nextParams.set("search", nextSearch);
      }

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams, urlSearch]
  );

  // Reusable function to load all segments
  const loadAllSegments = useCallback(async () => {
    try {
      const response = await fetchSegments({
        filterBy: "all",
        pageSize: 500,
      });
      setAllSegments(response.data ?? []);
    } catch (error) {
      console.error("Failed to load segments:", error);
    }
  }, []);

  // Preload all segments on page mount
  useEffect(() => {
    loadAllSegments();
  }, [loadAllSegments]);

  // Fetch scoring profiles when the scoring feature flag is enabled
  const fetchProfiles = useScoringStore((s) => s.fetchProfiles);
  useEffect(() => {
    if (!scoringEnabled) return;
    const email = getLoggedUserProfile()?.email;
    if (email) void fetchProfiles(email);
  }, [scoringEnabled, fetchProfiles]);

  // Scoring profile dialog state
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<ScoringProfile | null>(
    null
  );
  const [wizardOpen, setWizardOpen] = useState(false);
  const [generatedCriteria, setGeneratedCriteria] = useState<
    ScoringType[] | undefined
  >(undefined);
  const [generatedName, setGeneratedName] = useState<string | undefined>(
    undefined
  );

  // Refetch segments when segmentId changes (user switches segments)
  useEffect(() => {
    if (segmentId) {
      loadAllSegments();
    }
  }, [segmentId, loadAllSegments]);

  // Show info snackbar when entering create mode
  useEffect(() => {
    if (isCreateMode) {
      showInfo(
        "Configure your segment criteria, then click 'Create Segment' when ready."
      );
    }
  }, [isCreateMode, showInfo]);

  // Load segment data when in edit mode, clear when leaving
  useEffect(() => {
    if (segmentId) {
      setIsLoadingSegment(true);
      getSegment(segmentId)
        .then((seg) => {
          setSegment(seg);
          // Track recent segment access
          if (seg.id && seg.name) {
            addRecentSegment({ id: seg.id, name: seg.name });
            addRecentItem({
              id: seg.id,
              type: "segment",
              name: seg.name,
              route: `/segments/${seg.id}`,
            });
          }
        })
        .catch((err) => {
          if (err instanceof AxiosError) {
            const status = err.response?.status;
            if (status === 404) {
              showError(SEGMENT_ERROR_MESSAGES.NOT_FOUND);
            } else if (status === 403) {
              showError(SEGMENT_ERROR_MESSAGES.NO_ACCESS);
            } else {
              showError(SEGMENT_ERROR_MESSAGES.GENERIC);
            }
          } else {
            showError(SEGMENT_ERROR_MESSAGES.GENERIC);
          }
          navigate("/segments");
        })
        .finally(() => setIsLoadingSegment(false));
    } else {
      // Clear segment state when navigating away from edit mode
      setSegment(null);
    }
  }, [segmentId, showError, navigate, addRecentItem]);

  // Hydrate filters when segment loads (edit mode) or clear on create/explore
  useEffect(() => {
    if (segment?.metadata?.filters) {
      hydrateFromCompanyFilters(safeParseFilters(segment.metadata.filters));
    } else if (segmentId === undefined) {
      clearAll();
    }
  }, [
    segment?.metadata?.filters,
    segmentId,
    hydrateFromCompanyFilters,
    clearAll,
  ]);

  const handleSaveClick = useCallback(() => {
    saveDialog.open();
  }, [saveDialog]);

  const handleSaveFilters = useCallback(async () => {
    if (!segmentId) return;
    try {
      await updateSegment(segmentId, {
        metadata: { filters: serializeAppliedToJson() },
      });
      showSuccess("Segment saved successfully");
      markAsPersisted();
    } catch {
      showError("Failed to save segment");
    }
  }, [
    segmentId,
    serializeAppliedToJson,
    showSuccess,
    showError,
    markAsPersisted,
  ]);

  const handleSaveComplete = useCallback(
    (newSegmentId?: string) => {
      saveDialog.close();
      if (newSegmentId) {
        // Mark as saving to prevent navigation blocker from triggering
        // Use ref for synchronous update (state updates are async)
        isSavingRef.current = true;
        // Navigate to the edit mode with the new segment ID
        navigate(`/segments/${newSegmentId}`);
      }
    },
    [saveDialog, navigate]
  );

  // Menu handlers (edit mode)
  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setMenuAnchorEl(event.currentTarget);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleSaveAsNewClick = useCallback(() => {
    handleMenuClose();
    saveDialog.open();
  }, [handleMenuClose, saveDialog]);

  // Map save actions to their respective handlers (single source of truth)
  const saveActionHandlers = useMemo(
    () => ({
      create: handleSaveClick,
      save: handleSaveClick,
      saveFilters: handleSaveFilters,
      saveAsNew: handleSaveAsNewClick,
    }),
    [handleSaveClick, handleSaveFilters, handleSaveAsNewClick]
  );

  const handleEditClick = useCallback(() => {
    handleMenuClose();
    if (segment) {
      editDialog.open(segment);
    }
  }, [handleMenuClose, segment, editDialog]);

  const handleDeleteClick = useCallback(() => {
    handleMenuClose();
    deleteDialog.open();
  }, [handleMenuClose, deleteDialog]);

  const handleConfirmDelete = useCallback(async () => {
    if (!segmentId) return;
    setIsDeleting(true);
    try {
      await deleteSegment(segmentId);
      showSuccess("Segment deleted successfully");
      navigate("/segments");
    } catch {
      showError("Failed to delete segment");
    } finally {
      setIsDeleting(false);
      deleteDialog.close();
    }
  }, [segmentId, showSuccess, showError, navigate, deleteDialog]);

  const handleEditComplete = useCallback(() => {
    editDialog.close();
    // Reload segment data and refresh dropdown list
    if (segmentId) {
      setIsLoadingSegment(true);
      getSegment(segmentId)
        .then(setSegment)
        .catch(console.error)
        .finally(() => setIsLoadingSegment(false));
      loadAllSegments();
    }
  }, [segmentId, editDialog, loadAllSegments]);

  // Navigation blocker dialog handlers
  const handleBlockerSave = useCallback(async () => {
    if (isCreateMode) {
      // In create mode, open the save dialog instead of saving directly
      saveDialog.open();
      reset(); // Cancel the navigation - user will navigate after creating segment
    } else if (segmentId) {
      // Check if user has edit permission
      if (!canEdit) {
        // User doesn't have permission to update - offer "save as new" instead
        saveDialog.open();
        reset(); // Cancel the navigation - user will navigate after creating segment
        return;
      }

      // In edit mode with permission, save directly then proceed
      isSavingRef.current = true;
      setIsSaving(true);
      try {
        await updateSegment(segmentId, {
          metadata: { filters: serializeAppliedToJson() },
        });
        markAsPersisted();
        proceed();
      } catch {
        showError("Failed to save segment");
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    }
  }, [
    isCreateMode,
    segmentId,
    canEdit,
    saveDialog,
    reset,
    proceed,
    serializeAppliedToJson,
    markAsPersisted,
    showError,
  ]);

  const handleBlockerDiscard = useCallback(() => {
    proceed();
  }, [proceed]);

  const handleBlockerClose = useCallback(() => {
    reset();
  }, [reset]);

  // The segment is saveable whenever filters are applied. Dirtiness
  // deliberately does NOT gate this — an opened segment hydrates clean
  // (persistedFilters === appliedFilters), which used to leave the button
  // disabled while the toolbar showed active filters and results (RGI-2461).
  const isSaveButtonDisabled = !appliedFilters;
  const isMissingExportFilters = exportRequiresFilters && !appliedFilters;
  const isExportDisabled = isMissingExportFilters || !canExport;
  const exportTooltipTitle = !canExport
    ? exportDisabledMessage
    : isMissingExportFilters
      ? "Apply filters to enable export"
      : "";

  const ActiveFilterComponent =
    activeFilter && isModuleKey(activeFilter)
      ? modules[activeFilter].Component
      : null;

  const handleChipClick = useCallback((filterKey: FilterKey) => {
    setActiveFilter(filterKey);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setActiveFilter(null);
  }, []);

  const handleLocalizedInstallsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLocalizeInstalls(event.target.checked);
    },
    [setLocalizeInstalls]
  );

  const filterPanelWidth = match(activeFilter)
    .with("products", "vendors", () => 600)
    .with("targetOrganizations", "uploadedAccounts", () => 400)
    .with("intent", () => 400)
    .otherwise(() => 300);

  return (
    <Stack direction="row" height="100%" overflow="hidden">
      <FiltersSidebar
        activeFilter={activeFilter}
        onFilterSelect={setActiveFilter}
        getFilterCount={getFilterCount}
        categories={filteredCategories}
        header={
          scoringEnabled ? (
            <Suspense fallback={null}>
              <ScoringPanel
                onAddProfile={() => {
                  // Without the chooser, authoring goes straight to the admin
                  // AI-scoring tool; fall back if that URL can't be built.
                  if (!showMethodModal && openAiScoring(getTenantId())) return;
                  setWizardOpen(true);
                }}
                onEditProfile={(p) => {
                  setEditingProfile(p);
                  setProfileDialogOpen(true);
                }}
              />
            </Suspense>
          ) : undefined
        }
        footer={
          <LocalizedInstallsFooter
            checked={localizeInstalls}
            onChange={handleLocalizedInstallsChange}
          />
        }
      />

      {activeFilter && (
        <FilterPanel
          title={FILTER_TITLES[activeFilter]}
          onClose={handleCloseFilterPanel}
          width={filterPanelWidth}
        >
          {ActiveFilterComponent ? (
            <ActiveFilterComponent />
          ) : (
            <Typography variant="body2" color="text.secondary">
              This filter is not yet implemented.
            </Typography>
          )}
        </FilterPanel>
      )}

      <Stack
        direction="column"
        padding={3}
        flex={1}
        minWidth={0}
        overflow="hidden"
        sx={{ overflowX: "auto" }}
      >
        <Box pl={1} mb={1} minWidth={0}>
          <SegmentBreadcrumb
            segmentId={segmentId}
            segmentName={segment?.name}
            isLoading={isLoadingSegment}
            mode={breadcrumbMode}
            allSegments={allSegments}
          />
        </Box>
        <Box
          pl={1}
          mb={1}
          display="grid"
          gridTemplateColumns="minmax(0, 1fr) auto"
          alignItems="center"
          columnGap={2}
          minWidth={0}
        >
          <Typography
            component="h1"
            fontSize="26px"
            fontWeight={300}
            lineHeight={1.15}
            minWidth={0}
            noWrap
            title={isLoadingSegment ? undefined : pageTitle}
          >
            {isLoadingSegment ? <Skeleton width={200} /> : pageTitle}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: "max-content" }}
          >
            <CreditProgressBar loadingVariant="skeleton" compact />
            <Tooltip title={exportTooltipTitle}>
              <span>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<DownloadIcon />}
                  disabled={isExportDisabled}
                  onClick={() => exportDialog.open()}
                  id="segment-export-btn"
                >
                  Export
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                isSaveButtonDisabled ? "Apply filters to enable saving" : ""
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={isSaveButtonDisabled}
                  onClick={saveActionHandlers[saveButtonConfig.action]}
                  sx={{ whiteSpace: "nowrap" }}
                  id="segment-save-btn"
                >
                  {saveButtonConfig.label}
                </Button>
              </span>
            </Tooltip>

            {isEditMode &&
              (menuActions.showSaveAsNew ||
                menuActions.showEdit ||
                menuActions.showDelete) && (
                <>
                  <IconButton
                    size="small"
                    onClick={handleMenuOpen}
                    id="segment-more-menu-btn"
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    id="segment-more-menu"
                    slotProps={{
                      paper: {
                        sx: { minWidth: 180 },
                      },
                    }}
                  >
                    {menuActions.showSaveAsNew && (
                      <MenuItem
                        onClick={handleSaveAsNewClick}
                        id="segment-save-as-new-item"
                      >
                        <ListItemIcon>
                          <SaveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Save as new</ListItemText>
                      </MenuItem>
                    )}
                    {menuActions.showDivider && <Divider />}
                    {menuActions.showEdit && (
                      <MenuItem
                        onClick={handleEditClick}
                        id="segment-edit-details-item"
                      >
                        <ListItemIcon>
                          <EditOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Edit Details</ListItemText>
                      </MenuItem>
                    )}
                    {menuActions.showDelete && (
                      <MenuItem
                        onClick={handleDeleteClick}
                        id="segment-delete-item"
                      >
                        <ListItemIcon>
                          <DeleteOutlineIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
          </Stack>
        </Box>

        <Box pl={1} mb={2} minWidth={0}>
          <CompaniesExploreSearchInput
            initialValue={urlSearch}
            onDebouncedChange={handleDebouncedSearchChange}
          />
        </Box>

        <FiltersChipsBar
          chips={enrichedChips}
          onClearAll={clearAll}
          onChipClick={handleChipClick}
        />

        <CompaniesExploreList
          filters={appliedFilters}
          fuzzySearch={urlSearch}
          localizeInstalls={localizeInstalls}
          onCountChange={(count) => setCompanyCount(count ?? 0)}
          onEditProfile={(p) => {
            setEditingProfile(p);
            setProfileDialogOpen(true);
          }}
          onActiveSortChange={handleActiveSortChange}
        />

        <SegmentDialog
          open={saveDialog.isOpen}
          mode="create"
          title={isEditMode ? "Save as New Segment" : undefined}
          onClose={saveDialog.close}
          onSave={handleSaveComplete}
          filtersJson={serializeAppliedToJson()}
        />

        <SegmentModificationDialogs
          deleteDialog={deleteDialog}
          editDialog={editDialog}
          onDelete={handleConfirmDelete}
          onEditSave={handleEditComplete}
          isDeleting={isDeleting}
          segmentName={segment?.name}
        />

        <CompanyExportModal
          open={exportDialog.isOpen}
          onClose={exportDialog.close}
          filters={
            appliedFilters && {
              ...appliedFilters,
              fuzzySearch: urlSearch.trim(),
            }
          }
          companyCount={companyCount}
          localizeInstalls={localizeInstalls}
          activeSortEntry={activeSortEntry}
        />

        <UnsavedSegmentChangesDialog
          open={isBlocked}
          onClose={handleBlockerClose}
          onDiscard={handleBlockerDiscard}
          onSave={handleBlockerSave}
          isSaving={isSaving}
          isCreateMode={isCreateMode}
        />

        {scoringEnabled && (
          <Suspense fallback={null}>
            <ScoringProfileDialog
              open={profileDialogOpen}
              profile={editingProfile}
              initialCriteria={generatedCriteria}
              initialName={generatedName}
              onClose={() => {
                setProfileDialogOpen(false);
                setGeneratedCriteria(undefined);
                setGeneratedName(undefined);
              }}
              onSaved={() => {
                setProfileDialogOpen(false);
                setGeneratedCriteria(undefined);
                setGeneratedName(undefined);
              }}
            />
          </Suspense>
        )}
        {scoringEnabled && (
          <Suspense fallback={null}>
            <ScoringWizardDialog
              open={wizardOpen}
              onClose={() => setWizardOpen(false)}
              segments={allSegments}
              onCreateWithAi={() => openAiScoring(getTenantId())}
              onBuildManually={() => {
                setWizardOpen(false);
                setEditingProfile(null);
                setProfileDialogOpen(true);
              }}
              onGenerated={(criteria, name) => {
                setWizardOpen(false);
                setEditingProfile(null);
                setGeneratedCriteria(criteria);
                setGeneratedName(name);
                setProfileDialogOpen(true);
              }}
            />
          </Suspense>
        )}
      </Stack>
    </Stack>
  );
};

export const CompaniesExploreLegacyPage: FC = () => {
  const [searchCompanies] = useLazyQuery<SearchCompaniesV2Response>(
    SEARCH_COMPANY_QUERY,
    { context: { clientName: COMPANY_SEARCH_CLIENT } }
  );
  const [fetchCompaniesByIds] = useLazyQuery<CompanySearchResponse>(
    COMPANY_SEARCH_SLIM_QUERY,
    { context: { clientName: COMPANY_SEARCH_CLIENT } }
  );
  const [uploadFile] = useUploadLazyQuery({
    context: { clientName: COMPANY_MATCH_CLIENT },
    fetchPolicy: "no-cache",
  });
  const [fetchStats] = useStatsLazyQuery({
    context: { clientName: COMPANY_MATCH_CLIENT },
    fetchPolicy: "no-cache",
  });

  const {
    countries,
    regions,
    states,
    spendCategories,
    hgIndustries,
    sic,
    naics,
    products,
    vendors,
    productCategories,
    productAttributes,
  } = useTaxonomyContext();

  // Intent filter metadata queries
  const { data: journeysData } = useGetIntentBuyersJourneyQuery({
    context: { clientName: TAXONOMY_CLIENT },
  });
  const { data: contextsData } = useGetIntentContextTypesQuery({
    context: { clientName: TAXONOMY_CLIENT },
  });

  const geographyOptions = useMemo<GeographyOptions>(() => {
    const countryOptions = countries
      .filter((c) => c.alpha2 && c.name)
      .map((c) => ({
        code: c.alpha2,
        name: c.name,
        regionId: c.region?.id as RegionId | undefined,
      }));

    const regionOptions = regions
      .filter(
        (r) => r.id && (r.id === "AMER" || r.id === "EMEA" || r.id === "APAC")
      )
      .map((r) => ({
        id: r.id as RegionId,
        label: `${r.id} (${r.name.toUpperCase()})`,
      }));

    return {
      regions: regionOptions,
      countries: countryOptions,
    };
  }, [countries, regions]);

  const itSpendOptions = useMemo<ItSpendOptions>(
    () => ({
      spendCategories,
    }),
    [spendCategories]
  );

  const intentOptions = useMemo<IntentOptions>(
    () => ({
      buyersJourneyOptions: (journeysData?.intentBuyersJourney?.data ?? [])
        .filter((j): j is NonNullable<typeof j> => j !== null && j.id !== null)
        .map((j) => ({
          id: j.id as string,
          name: j.name ?? "Unknown",
          description: j.description ?? undefined,
        })),
      contextTypeOptions: (contextsData?.intentContextType?.data ?? [])
        .filter((c): c is NonNullable<typeof c> => c !== null && c.id !== null)
        .map((c) => ({
          id: c.id as string,
          name: c.name ?? "Unknown",
          description: c.description ?? undefined,
        })),
    }),
    [journeysData, contextsData]
  );

  const filterLookups = useMemo<FilterLookups>(
    () =>
      buildCompanyFilterLookups({
        countries,
        states,
        hgIndustries,
        sic,
        naics,
        products,
        vendors,
        productCategories,
        productAttributes,
        spendCategories,
        buyersJourneys: intentOptions.buyersJourneyOptions,
        contextTypes: intentOptions.contextTypeOptions,
      }),
    [
      hgIndustries,
      sic,
      naics,
      products,
      vendors,
      productCategories,
      productAttributes,
      spendCategories,
      countries,
      states,
      intentOptions.buyersJourneyOptions,
      intentOptions.contextTypeOptions,
    ]
  );

  const targetOrganizationsOptions = useMemo<TargetOrganizationsOptions>(
    () => ({
      onSearch: async (query: string) => {
        const result = await searchCompanies({
          variables: {
            query,
            per: 15,
            sort: [{ field: "REVENUE_RANGE_MIN", direction: "DESC" as const }],
          },
        });
        const companies = result.data?.searchCompaniesV2?.data ?? [];
        return companies.map(spogCompanyToCached);
      },
      onHydrate: async (ids: string[]) => {
        if (ids.length === 0) return [];
        const result = await fetchCompaniesByIds({
          variables: { filters: { hgIds: ids }, per: ids.length },
        });
        const companies = result.data?.search?.data ?? [];
        return companies.map(spogCompanyToCached);
      },
    }),
    [fetchCompaniesByIds, searchCompanies]
  );

  const uploadedAccountsOptions = useMemo<UploadedAccountsOptions>(
    () => ({
      onUpload: async (
        file: File,
        _includeHierarchy: boolean,
        enforceCountryMatch: boolean
      ) => {
        const result = await uploadFile({
          variables: {
            file,
            isPlatformPermissible: true,
            enforceCountryMatch,
          },
        });

        // Check for GraphQL errors first
        if (result.errors?.length) {
          const errorMessage = result.errors.map((e) => e.message).join("; ");
          throw new Error(errorMessage || "Upload failed");
        }

        if (!result.data?.upload?.fileId) {
          throw new Error("Upload failed - no file ID returned");
        }
        return {
          fileId: result.data.upload.fileId,
          fileName: result.data.upload.fileName ?? file.name,
          totalRows: result.data.upload.totalRows ?? null,
        };
      },
      onPoll: async (fileId: string) => {
        const result = await fetchStats({
          variables: { fileId },
        });
        if (!result.data?.stats) {
          throw new Error("Failed to fetch stats");
        }
        return {
          fileId: result.data.stats.fileId ?? fileId,
          fileName: result.data.stats.fileName ?? null,
          totalRows: result.data.stats.totalRows ?? null,
          matchedRows: result.data.stats.matchedRows ?? null,
          invalidRows: result.data.stats.invalidRows ?? null,
          uploaded: result.data.stats.uploaded ?? null,
          lastMatched: result.data.stats.lastMatched ?? null,
        };
      },
      onDownloadDetails: downloadMatchDetails,
    }),
    [uploadFile, fetchStats]
  );

  return (
    <CompanyFilterProvider
      options={{
        geography: geographyOptions,
        itSpend: itSpendOptions,
        targetOrganizations: targetOrganizationsOptions,
        uploadedAccounts: uploadedAccountsOptions,
        intent: intentOptions,
      }}
      lookups={filterLookups}
    >
      <CompaniesExplorePageContent />
    </CompanyFilterProvider>
  );
};
